import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { useProject } from '../contexts/ProjectContext';
import { useAuth } from '../auth/useAuth';
import { useNavigate } from 'react-router-dom';

const ProjectList = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const {
    projects,
    activeProject,
    loading,
    error,
    createProject,
    updateProject,
    deleteProject,
    switchProject
  } = useProject();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    netivimProjectId: '',
    derechProjectId: ''
  });
  const [formLoading, setFormLoading] = useState(false);

  const handleOpenDialog = (project = null) => {
    setEditingProject(project);
    if (project) {
      setFormData({
        name: project.name || '',
        netivimProjectId: project.netivimProjectId || '',
        derechProjectId: project.derechProjectId || ''
      });
    } else {
      setFormData({
        name: '',
        netivimProjectId: '',
        derechProjectId: ''
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingProject(null);
    setFormData({
      name: '',
      netivimProjectId: '',
      derechProjectId: ''
    });
  };

  const handleSubmit = async () => {
    try {
      setFormLoading(true);
      
      if (editingProject) {
        await updateProject(editingProject.id, formData);
      } else {
        await createProject(formData);
      }
      
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving project:', error);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (projectId) => {
    if (window.confirm('האם אתה בטוח שברצונך למחוק את הפרויקט?')) {
      try {
        await deleteProject(projectId);
      } catch (error) {
        console.error('Error deleting project:', error);
      }
    }
  };


  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          ניהול פרויקטים
        </Typography>
        
        {user?.role === 'manager' && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            פרויקט חדש
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {projects.map((project) => (
          <Grid item xs={12} md={6} lg={4} key={project.id}>
            <Card 
              sx={{ 
                height: '100%',
                border: activeProject?.id === project.id ? 2 : 0,
                borderColor: 'primary.main'
              }}
            >
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <Typography variant="h6" component="h2" gutterBottom>
                    {project.name}
                  </Typography>
                  
                  {activeProject?.id === project.id && (
                    <Chip
                      label="פעיל"
                      color="success"
                      size="small"
                    />
                  )}
                </Box>

                <Box display="flex" gap={1} mb={2}>
                  {project.netivimProjectId && (
                    <Chip 
                      label={`נתיבים: ${project.netivimProjectId}`}
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                  )}
                  {project.derechProjectId && (
                    <Chip 
                      label={`דרך: ${project.derechProjectId}`}
                      size="small"
                      variant="outlined"
                      color="secondary"
                    />
                  )}
                </Box>

                <Box mt={2}>
                  <Typography variant="body2" color="text.secondary">
                    אוטובוסים: {project.buses?.length || 0} | 
                    תיירים: {project.tourists?.length || 0}
                  </Typography>
                </Box>
              </CardContent>

              <CardActions>
                <Box sx={{ flexGrow: 1 }} />

                <Tooltip title="עריכה">
                  <IconButton
                    size="small"
                    onClick={() => handleOpenDialog(project)}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>

                {user?.role === 'manager' && (
                  <Tooltip title="מחיקה">
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(project.id)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Dialog ליצירה/עריכה */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingProject ? 'עריכת פרויקט' : 'פרויקט חדש'}
        </DialogTitle>
        
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="שם הפרויקט"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              fullWidth
              required
            />
            
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="מזהה פרויקט נתיבים"
                  value={formData.netivimProjectId}
                  onChange={(e) => setFormData(prev => ({ ...prev, netivimProjectId: e.target.value }))}
                  fullWidth
                />
              </Grid>
              
              <Grid item xs={6}>
                <TextField
                  label="מזהה פרויקט דרך"
                  value={formData.derechProjectId}
                  onChange={(e) => setFormData(prev => ({ ...prev, derechProjectId: e.target.value }))}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            ביטול
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={formLoading || !formData.name}
          >
            {formLoading ? <CircularProgress size={20} /> : 'שמירה'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProjectList;
